export const YARDS_PER_METER = 1.09361
export const METERS_PER_YARD = 0.9144
export const FEET_PER_YARD = 3
export const FEET_PER_METER = YARDS_PER_METER * FEET_PER_YARD
export const SQUARE_FEET_PER_SQUARE_METER = 10.7639104167

export const isImperialClub = (club) => Boolean(club && club.is_imperial)

export const metersToYards = (meters) => meters * YARDS_PER_METER
export const yardsToMeters = (yards) => yards * METERS_PER_YARD
export const metersToFeet = (meters) => meters * FEET_PER_METER
export const squareMetersToSquareFeet = (squareMeters) => squareMeters * SQUARE_FEET_PER_SQUARE_METER

const formatNumber = (value, decimals = 1) => {
  if (decimals <= 0) {
    return `${Math.round(value)}`
  }
  return value.toFixed(decimals)
}

export const getDistanceUnitsByPriority = (isImperial) => (isImperial ? ['yd', 'm'] : ['m', 'yd'])

export const formatDistanceWithUnit = (meters, unit = 'm', decimals = 1) => {
  if (unit === 'yd') {
    return `${formatNumber(metersToYards(meters), decimals)} yd`
  }
  return `${formatNumber(meters, decimals)} m`
}

// Metamap helper: always return exactly two lines (meters + yards), never feet.
export const formatDualDistanceValueLines = (meters, options = {}) => {
  const {
    isImperial = false,
    metricDecimals = 1,
    imperialDecimals = 1
  } = options

  const [primaryUnit, secondaryUnit] = getDistanceUnitsByPriority(isImperial)
  const primaryDecimals = primaryUnit === 'yd' ? imperialDecimals : metricDecimals
  const secondaryDecimals = secondaryUnit === 'yd' ? imperialDecimals : metricDecimals

  return {
    primary: formatDistanceWithUnit(meters, primaryUnit, primaryDecimals),
    secondary: formatDistanceWithUnit(meters, secondaryUnit, secondaryDecimals)
  }
}

export const distanceForUiFromMeters = (meters, isImperial) => {
  if (isImperial) {
    return Math.round(metersToYards(meters))
  }
  return Math.round(meters)
}

export const distanceForStorageFromUi = (distance, isImperial) => {
  if (isImperial) {
    return Math.round(yardsToMeters(distance))
  }
  return Math.round(distance)
}

export const formatDistanceDisplay = (meters, options = {}) => {
  const {
    isImperial = false,
    shortImperialThresholdYards = 25,
    includeFeetWhenShort = false,
    metricDecimals = 1,
    imperialDecimals = 1,
    feetDecimals = 1
  } = options

  if (!isImperial) {
    return `${meters.toFixed(metricDecimals)} m`
  }

  const yards = metersToYards(meters)
  if (!includeFeetWhenShort || yards >= shortImperialThresholdYards) {
    return `${yards.toFixed(imperialDecimals)} yd`
  }

  const feet = metersToFeet(meters)
  return `${yards.toFixed(imperialDecimals)} yd (${feet.toFixed(feetDecimals)} ft)`
}

export const formatDualDistanceDisplay = (meters, options = {}) => {
  const {
    shortImperialThresholdYards = 25,
    metricDecimals = 1,
    imperialDecimals = 1,
    feetDecimals = 1
  } = options

  const metricLine = `${meters.toFixed(metricDecimals)} m`
  const yards = metersToYards(meters)

  if (yards >= shortImperialThresholdYards) {
    return `${metricLine}\n${yards.toFixed(imperialDecimals)} yd`
  }

  const feet = metersToFeet(meters)
  return `${metricLine}\n${yards.toFixed(imperialDecimals)} yd (${feet.toFixed(feetDecimals)} ft)`
}

export const formatAreaDisplay = (squareMeters, options = {}) => {
  const {
    isImperial = false,
    areaDecimals = 2,
    hectaresThreshold = 1000
  } = options

  if (isImperial) {
    const squareFeet = squareMetersToSquareFeet(squareMeters)
    return `${squareFeet.toFixed(areaDecimals)} sqft`
  }

  if (squareMeters < hectaresThreshold) {
    return `${squareMeters.toFixed(areaDecimals)} m²`
  }

  return `${(squareMeters / 10000).toFixed(areaDecimals)} ha`
}
